import { FC, useCallback } from 'react'
import cn from 'classnames'
import * as styles from 'styles/base/utilities.css'
import * as themes from 'styles/base/themes.styl'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { capitalizeFirstLetter, formatUSCurrency } from 'helpers/formatHelpers'
import { LabeledElement, LabeledElementList } from 'components/atoms/LabeledElement/LabeledElement'
import Separator from 'components/atoms/Separator/Separator'
import { TransactionUid, YODLEE_TRANSACTION_TYPE } from 'interfaces/moneyflow'
import { recentCardTransactionSelector, spendingTransactionsSelector } from 'selectors/moneyflowSelectors'
import { TransactionCategory } from 'components/features/pfm/TransactionCategory/TransactionCategory'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { formatRelativeDate } from 'helpers/dateTimeHelpers'
import Flex from 'components/atoms/Flex/Flex'
import moment from 'moment'
import { DATE_FORMAT_READABLE } from 'constants/datetime'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { CANCEL_BUTTON_PROPS, KillAllPanesButton } from 'components/features/pfm/Button/FloatingPaneButtons'

type TransactionDetailsPaneProps = {
  uid: TransactionUid
}

const TransactionDetailsPane: FC<TransactionDetailsPaneProps> = ({ uid }) => {
  const recentTransaction = useStoreSelector((state) => recentCardTransactionSelector(state, { uid }))
  const spendingTransactions = useStoreSelector(spendingTransactionsSelector)
  const transaction = !!spendingTransactions ? getItemFromCollection(spendingTransactions, uid) : recentTransaction
  const dispatch = useStoreDispatch()

  const handleCategoryClick = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('transaction-recat', { mode: 'category', transaction }))
  }, [dispatch, transaction])


  if (!transaction) {
    return <LoadingOverlay />
  }

  const { category, status, amount, description, date } = transaction
  const verboseDescription = transaction.account_source_type === YODLEE_TRANSACTION_TYPE
    ? transaction.description_verbose
    : description
  const isPending = status === 'pending'

  const transactionCategoryChild = (
    <TransactionCategory
      category={ category }
      onClick={ handleCategoryClick }
      dim={ isPending }
      disabled={ isPending }
      tooltipText={ undefined }
    />
  )

  const statusChild = (
    <span className={ cn(themes.textOnly, { [themes.orange]: isPending }) } >
      { capitalizeFirstLetter(status) }
    </span>
  )

  return (
    <CreditCardPanePreset
      title='Transaction Details'
      secondaryButton={ null }
      tertiaryButton={ <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } text='Close' /> }
      bodyClassName={ styles.textCenter }
    >
      <Flex gap='micro' marginBottom='defaultSize'>
        <LabeledElement
          label={ formatRelativeDate(date) }
          element={ formatUSCurrency(amount, 'penny') }
          theme='hero'
          vertical
        />
        <h3><strong>{ description }</strong></h3>
      </Flex>
      <LabeledElementList>
        <LabeledElement label='Status' element={ statusChild } />
        <LabeledElement label='Transaction Date' element={ moment(date).format(DATE_FORMAT_READABLE) } />
        <Separator />
        <LabeledElement label='Amount' element={ formatUSCurrency(amount, 'penny') } />
        <LabeledElement label='Description' element={ verboseDescription } />
        <LabeledElement label='Category' element={ transactionCategoryChild } />
      </LabeledElementList>
      <small>
        If you wish to dispute this transaction please call (844) 333-3110.
      </small>
    </CreditCardPanePreset>
  )
}

export default TransactionDetailsPane
